import type { ProjectCustomData } from '~/api/projectCustomData';
import type { TOCChild, TOCParent } from '~/common/types';
import {
  CUSTOM_DATA_LAYER_PREFIX,
  customDataLayerId,
  renderableShapefileRows,
} from '../../map/customDataLayers.helpers';
import { toMapPreviewLayerId } from './siteDataLayers';

export const CUSTOM_DATA_SECTION_LABEL = 'Custom Data';
/** The picker keys this section's expansion by it (see `expansionKey`). */
export const CUSTOM_DATA_SECTION_ID = 'custom-data-download';

/**
 * Picker section for the FME extract tab: one leaf per READY shapefile layer, ids prefixed
 * `customData:` (the server authorizes/partitions on that prefix). WMTS is never downloadable
 * (licensing), non-ready rows aren't exportable. Undefined when there is nothing to show, so no
 * empty group renders.
 */
// TOCParent with children narrowed to leaves: this section is always flat, and consumers map child.id.
export type CustomDataPickerSection = Omit<TOCParent, 'children'> & { children: TOCChild[] };

export function buildCustomDataPickerSection(
  rows: readonly ProjectCustomData[] | undefined,
): CustomDataPickerSection | undefined {
  const children = renderableShapefileRows(rows).map((row) => ({
    id: customDataLayerId(row.id),
    label: row.label,
  }));
  if (children.length === 0) return undefined;
  return { label: CUSTOM_DATA_SECTION_LABEL, expanded: true, sectionId: CUSTOM_DATA_SECTION_ID, children };
}

/**
 * Selected custom-data ids the picker no longer offers (row archived or hidden mid-session): the
 * leaf vanishes but the id would linger in the selection and 403 the whole submit with no way to
 * untick it. Returns nothing unless the rows query has RESOLVED — a loading or error state must
 * never wipe the user's picks.
 */
export function findStaleCustomDataSelections(
  selectedLayerIds: Iterable<string>,
  section: TOCParent | undefined,
  rowsQueryResolved: boolean,
): string[] {
  if (!rowsQueryResolved) return [];
  const offered = new Set((section?.children ?? []).map((child) => ('id' in child ? child.id : '')));
  return Array.from(selectedLayerIds).filter((id) => id.startsWith(CUSTOM_DATA_LAYER_PREFIX) && !offered.has(id));
}

/**
 * Drop stale ids from the download selection and the map's visible set. Both are valtio proxySets —
 * mutate in place; reassigning would swap in an untracked plain Set and kill re-renders.
 * The visible set is keyed by map id, so ids go through `toMapPreviewLayerId` (a pass-through for
 * everything but `siteData:` leaves, whose preview layer renders off the bare catalog key).
 */
export function pruneStaleSelections(staleIds: string[], selectedLayerIds: Set<string>, visibleLayerIds: Set<string>) {
  for (const id of staleIds) {
    selectedLayerIds.delete(id);
    visibleLayerIds.delete(toMapPreviewLayerId(id));
  }
}

export type CustomDataPickerState = {
  section: CustomDataPickerSection | undefined;
  /** Ids to publish as `availableCustomLayerIds`; `undefined` means leave the published set untouched. */
  availableIds: string[] | undefined;
  staleIds: string[];
};

/**
 * Whether and what the extract picker offers for custom data, given `export-custom-data-enabled`.
 * Anything short of resolved-and-enabled hides the section and empties the available set regardless
 * of the rows query, so a slow fetch can't leave the lightning menu able to select custom layers.
 * Pruning is the narrower case: selections are dropped only once a flags response for the active org
 * says the flag is off, never while it is merely unresolved. Flag on publishes only on resolved rows
 * — a loading or error state must never wipe the user's picks.
 */
export function resolveCustomDataPickerState({
  flagResolved,
  flagEnabled,
  rows,
  rowsResolved,
  selectedLayerIds,
}: {
  flagResolved: boolean;
  flagEnabled: boolean;
  rows: readonly ProjectCustomData[] | undefined;
  rowsResolved: boolean;
  selectedLayerIds: Iterable<string>;
}): CustomDataPickerState {
  if (!(flagResolved && flagEnabled)) {
    return {
      section: undefined,
      availableIds: [],
      staleIds: flagResolved ? findStaleCustomDataSelections(selectedLayerIds, undefined, true) : [],
    };
  }

  const section = buildCustomDataPickerSection(rows);
  return {
    section,
    availableIds: rowsResolved ? (section?.children.map((child) => child.id) ?? []) : undefined,
    staleIds: findStaleCustomDataSelections(selectedLayerIds, section, rowsResolved),
  };
}
